import { useCallback, useEffect, useState } from 'react';

import {
  createChannelAdjust,
  createChannelDeposit,
  createChannelExternalUse,
  createChannelRestore,
  deleteChannelLedgerEntry,
  getChannelBalances,
  getChannelLedger,
  getUnrecordedDeliveries,
  reconcileChannel,
  repairUnrecordedDeliveries,
} from '@shared/api/giftChannelLedger/giftChannelLedger.controller';
import {
  ChannelBalanceSummary,
  ChannelLedgerListResponse,
  ChannelReconcileResponse,
  GiftChannelLedgerEntry,
  GiftChannelLedgerType,
  LEDGER_TYPE_CONFIG,
  UnrecordedResponse,
} from '@shared/api/giftChannelLedger/giftChannelLedger.interface';
import { DeliveryChannel } from '@shared/api/giftCoupon/giftCoupon.interface';
import { useAuth } from '@shared/state/hooks/useAuth';

import {
  kstDatetimeLocalNow,
  kstDayEndISO,
  kstDayStartISO,
  parseKstDatetimeLocal,
} from '../utils/kstTime.utils';
import { useToast } from './useToast';

export interface ManualLedgerModalState {
  type: GiftChannelLedgerType;
  channel: DeliveryChannel;
  /** 원 단위 정수 입력값 (Adjust만 음수 허용) */
  amount: string;
  /** datetime-local 값 — 비우면 서버 현재시각 */
  occurredAt: string;
  memo: string;
  isSaving: boolean;
}

const errMsg = (e: unknown, fallback: string): string => {
  const msg = (e as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(msg)) return msg.join('\n');
  return msg || fallback;
};

const is404 = (e: unknown): boolean =>
  (e as { response?: { status?: number } })?.response?.status === 404;

export function useChannelLedgerPage() {
  const { isAdmin } = useAuth();
  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(2500);

  // ── 지원 여부·잔액 요약 ──
  // null=확인 중, false=구서버(엔드포인트 미배포, 404), true=사용 가능
  const [supported, setSupported] = useState<boolean | null>(null);
  const [balances, setBalances] = useState<ChannelBalanceSummary[] | null>(null);

  const refreshBalances = useCallback(async () => {
    try {
      setBalances(await getChannelBalances());
      setSupported(true);
    } catch (e) {
      if (is404(e)) {
        setSupported(false);
      } else {
        showToast(errMsg(e, '잔액 요약을 불러오지 못했습니다.'));
      }
    }
  }, [showToast]);

  useEffect(() => {
    refreshBalances();
  }, [refreshBalances]);

  // ── 원장 목록 (채널 탭 + 기간 필터) ──
  const [selectedChannel, setSelectedChannel] = useState<DeliveryChannel>(
    DeliveryChannel.Addcon,
  );
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [ledger, setLedger] = useState<ChannelLedgerListResponse | null>(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const refreshLedger = useCallback(async () => {
    setLedgerLoading(true);
    try {
      setLedger(
        await getChannelLedger(
          selectedChannel,
          kstDayStartISO(fromDate),
          kstDayEndISO(toDate),
        ),
      );
    } catch (e) {
      setLedger(null);
      if (!is404(e)) showToast(errMsg(e, '원장 목록을 불러오지 못했습니다.'));
    } finally {
      setLedgerLoading(false);
    }
  }, [selectedChannel, fromDate, toDate, showToast]);

  useEffect(() => {
    if (supported) refreshLedger();
  }, [supported, refreshLedger]);

  // ── 대사 (채널별 실잔액 입력 → diff) ──
  const [reconcileInputs, setReconcileInputs] = useState<
    Partial<Record<DeliveryChannel, string>>
  >({});
  const [reconcileResults, setReconcileResults] = useState<
    Partial<Record<DeliveryChannel, ChannelReconcileResponse>>
  >({});
  const [reconcilingChannel, setReconcilingChannel] = useState<DeliveryChannel | null>(
    null,
  );

  const setReconcileInput = useCallback((channel: DeliveryChannel, value: string) => {
    setReconcileInputs((prev) => ({ ...prev, [channel]: value }));
  }, []);

  const handleReconcile = useCallback(
    async (channel: DeliveryChannel) => {
      const raw = (reconcileInputs[channel] ?? '').replace(/[,\s원]/g, '');
      const actualBalance = Number(raw);
      if (raw === '' || !Number.isFinite(actualBalance)) {
        showToast('실제 사이트 잔액을 숫자로 입력해주세요.');
        return;
      }
      setReconcilingChannel(channel);
      try {
        const result = await reconcileChannel(channel, actualBalance);
        setReconcileResults((prev) => ({ ...prev, [channel]: result }));
      } catch (e) {
        showToast(errMsg(e, '대사 조회에 실패했습니다.'));
      } finally {
        setReconcilingChannel(null);
      }
    },
    [reconcileInputs, showToast],
  );

  // 잔액이 바뀌는 조작(기장·삭제·소급) 후에는 이전 대사 결과가 낡으므로 비운다
  const afterMutation = useCallback(() => {
    setReconcileResults({});
    refreshBalances();
    refreshLedger();
  }, [refreshBalances, refreshLedger]);

  // ── 수동 기장 모달 ──
  const [manualModal, setManualModal] = useState<ManualLedgerModalState | null>(null);

  const openManualModal = useCallback(
    (type: GiftChannelLedgerType, channel?: DeliveryChannel, memoPreset?: string) => {
      setManualModal({
        type,
        channel: channel ?? selectedChannel,
        amount: '',
        occurredAt: kstDatetimeLocalNow(),
        memo: memoPreset ?? '',
        isSaving: false,
      });
    },
    [selectedChannel],
  );

  const submitManualModal = useCallback(async () => {
    if (!manualModal || manualModal.isSaving) return;
    const { type, channel, occurredAt } = manualModal;
    const memo = manualModal.memo.trim();
    const amount = Number(manualModal.amount.replace(/[,\s원]/g, ''));

    if (!Number.isInteger(amount)) {
      showToast('금액을 정수(원)로 입력해주세요.');
      return;
    }
    if (type === GiftChannelLedgerType.Adjust) {
      if (amount === 0) {
        showToast('보정 금액은 0이 될 수 없습니다.');
        return;
      }
      if (!memo) {
        showToast('보정은 메모가 필수입니다.');
        return;
      }
    } else if (amount <= 0) {
      showToast('금액은 양수(원)로 입력해주세요. 부호는 유형이 결정합니다.');
      return;
    }

    const dto = {
      channel,
      amount,
      ...(occurredAt && { occurredAt: parseKstDatetimeLocal(occurredAt).toISOString() }),
      ...(memo && { memo }),
    };
    setManualModal((prev) => (prev ? { ...prev, isSaving: true } : null));
    try {
      if (type === GiftChannelLedgerType.Deposit) await createChannelDeposit(dto);
      else if (type === GiftChannelLedgerType.ExternalUse)
        await createChannelExternalUse(dto);
      else if (type === GiftChannelLedgerType.Restore) await createChannelRestore(dto);
      else await createChannelAdjust({ ...dto, memo });
      setManualModal(null);
      showToast(`${LEDGER_TYPE_CONFIG[type].label} 기록이 저장되었습니다.`);
      afterMutation();
    } catch (e) {
      showToast(errMsg(e, '기록 저장에 실패했습니다.'));
      setManualModal((prev) => (prev ? { ...prev, isSaving: false } : null));
    }
  }, [manualModal, showToast, afterMutation]);

  // ── 수동 항목 삭제 (Admin 한정) ──
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleDeleteEntry = useCallback(
    async (entry: GiftChannelLedgerEntry) => {
      if (
        !window.confirm(
          `[${
            LEDGER_TYPE_CONFIG[entry.type].label
          }] ${entry.amount.toLocaleString()}원 항목을 삭제하시겠습니까?\n삭제는 되돌릴 수 없습니다. (정정은 보정 입력을 권장)`,
        )
      )
        return;
      setDeletingId(entry._id);
      try {
        await deleteChannelLedgerEntry(entry._id);
        showToast('삭제되었습니다.');
        afterMutation();
      } catch (e) {
        showToast(errMsg(e, '삭제에 실패했습니다.'));
      } finally {
        setDeletingId(null);
      }
    },
    [showToast, afterMutation],
  );

  // ── 미기장 발송 점검 ──
  const [unrecordedOpen, setUnrecordedOpen] = useState(false);
  const [unrecordedFrom, setUnrecordedFrom] = useState('');
  const [unrecordedTo, setUnrecordedTo] = useState('');
  const [unrecorded, setUnrecorded] = useState<UnrecordedResponse | null>(null);
  const [unrecordedLoading, setUnrecordedLoading] = useState(false);
  const [isRepairing, setIsRepairing] = useState(false);

  const fetchUnrecorded = useCallback(async () => {
    setUnrecordedLoading(true);
    try {
      setUnrecorded(
        await getUnrecordedDeliveries(
          kstDayStartISO(unrecordedFrom),
          kstDayEndISO(unrecordedTo),
        ),
      );
    } catch (e) {
      setUnrecorded(null);
      showToast(errMsg(e, '미기장 조회에 실패했습니다.'));
    } finally {
      setUnrecordedLoading(false);
    }
  }, [unrecordedFrom, unrecordedTo, showToast]);

  const handleRepairUnrecorded = useCallback(async () => {
    if (!unrecorded || isRepairing) return;
    const recordable = unrecorded.groups.filter((g) => g.recordable);
    if (recordable.length === 0) return;
    const couponCount = recordable.reduce((sum, g) => sum + g.quantity, 0);
    if (
      !window.confirm(
        `${recordable.length}개 배치(쿠폰 ${couponCount}건)를 소급 기장하시겠습니까?\n도입 이전 레거시 발송분이 섞여 있으면 기초잔액과 이중 차감됩니다 — 조회 시작일을 도입일 이후로 확인하세요.`,
      )
    )
      return;
    setIsRepairing(true);
    try {
      const result = await repairUnrecordedDeliveries(
        kstDayStartISO(unrecordedFrom),
        kstDayEndISO(unrecordedTo),
      );
      showToast(
        `소급 기장 완료 — ${result.repairedGroups}배치 ${result.repairedCoupons}건` +
          (result.skippedGroups > 0
            ? ` (소급 불가 ${result.skippedGroups}배치 ${result.skippedCoupons}건)`
            : ''),
      );
      await fetchUnrecorded();
      afterMutation();
    } catch (e) {
      showToast(errMsg(e, '소급 기장에 실패했습니다.'));
    } finally {
      setIsRepairing(false);
    }
  }, [
    unrecorded,
    isRepairing,
    unrecordedFrom,
    unrecordedTo,
    fetchUnrecorded,
    afterMutation,
    showToast,
  ]);

  return {
    isAdminUser: isAdmin(),
    supported,
    balances,

    selectedChannel,
    setSelectedChannel,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    ledger,
    ledgerLoading,

    reconcileInputs,
    setReconcileInput,
    reconcileResults,
    reconcilingChannel,
    handleReconcile,

    manualModal,
    setManualModal,
    openManualModal,
    submitManualModal,

    deletingId,
    handleDeleteEntry,

    unrecordedOpen,
    setUnrecordedOpen,
    unrecordedFrom,
    setUnrecordedFrom,
    unrecordedTo,
    setUnrecordedTo,
    unrecorded,
    unrecordedLoading,
    fetchUnrecorded,
    isRepairing,
    handleRepairUnrecorded,

    toastMessage,
    toastDurationMs,
  };
}
